import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { fetchRecordCount, fetchUniqueUserCount } from './getNumOfGraph.js';
import './App.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

export function Overview() {
  const [recordsPeriod, setRecordsPeriod] = useState('This day');
  const [usersPeriod, setUsersPeriod] = useState('This day');
  const [recordsData, setRecordsData] = useState({
    'This day': { chart: [], number: 0 },
    'This week': { chart: [], number: 0 },
    'This month': { chart: [], number: 0 },
    'This year': { chart: [], number: 0 },
    '24h': { chart: [], number: 0 },
    '7 days': { chart: [], number: 0 },
    'All data': { chart: [], number: 0 },
  });
  const [usersData, setUsersData] = useState({
    'This day': { chart: [], number: 0 },
    'This week': { chart: [], number: 0 },
    'This month': { chart: [], number: 0 },
    'This year': { chart: [], number: 0 },
    '24h': { chart: [], number: 0 },
    '7 days': { chart: [], number: 0 },
    'All data': { chart: [], number: 0 },
  });

  const chartPeriods = ['This day', 'This week', 'This month', 'This year'];
  const allPeriods = ['This day', 'This week', 'This month', 'This year', '24h', '7 days', 'All data'];

  // Function to format date as YYYY-MM-DD HH:mm:ss
  const formatDateTime = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // Function to get current HKT time
  const getHKT = () => {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Hong_Kong" }));
  };

  // Function to get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Function to get day names for the current week up to today
  const getWeekDayNames = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hktNow = getHKT();
    const today = hktNow.getDay(); // 4 (Thu)
    const daysSinceMonday = today === 0 ? 6 : today - 1; // Days from Monday to today
    return days.slice(0, daysSinceMonday + 1); // ["Mon", "Tue", "Wed", "Thu"]
  };

  // Function to calculate time ranges for each period in HKT
  const getTimeRange = (period, index = null) => {
    const hktNow = getHKT();
    let startTime, endTime;

    switch (period) {
      case 'This day':
        if (index !== null) {
          // For hourly data (HH:00:00 to HH:59:59)
          startTime = new Date(hktNow);
          startTime.setHours(index, 0, 0, 0);
          endTime = new Date(startTime);
          endTime.setHours(index, 59, 59, 999);
        } else {
          // For the entire day (00:00:00 to 23:59:59)
          startTime = new Date(hktNow);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(hktNow);
          endTime.setHours(23, 59, 59, 999);
        }
        break;
      
      case 'This week':
        if (index !== null) {
          // For daily data in week (00:00:00 to 23:59:59)
          // Monday is index 0, Tuesday is index 1, ..., Thursday is index 3
          startTime = new Date(hktNow);
          const monday = hktNow.getDate() - (hktNow.getDay() === 0 ? 6 : hktNow.getDay() - 1);
          startTime.setDate(monday + index);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(startTime);
          endTime.setHours(23, 59, 59, 999);
        } else {
          // For the entire week (Monday 00:00:00 to current day 23:59:59)
          startTime = new Date(hktNow);
          const dayOfWeek = hktNow.getDay() === 0 ? 6 : hktNow.getDay() - 1;
          startTime.setDate(hktNow.getDate() - dayOfWeek);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(hktNow);
          endTime.setHours(23, 59, 59, 999);
        }
        break;
      
      case 'This month':
        if (index !== null) {
          // For daily data in month (00:00:00 to 23:59:59)
          startTime = new Date(hktNow.getFullYear(), hktNow.getMonth(), index + 1);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(startTime);
          endTime.setHours(23, 59, 59, 999);
        } else {
          // For the entire month (1st 00:00:00 to current day 23:59:59)
          startTime = new Date(hktNow.getFullYear(), hktNow.getMonth(), 1);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(hktNow);
          endTime.setHours(23, 59, 59, 999);
        }
        break;
      
      case 'This year':
        if (index !== null) {
          // For monthly data in year (1st 00:00:00 to last day 23:59:59)
          startTime = new Date(hktNow.getFullYear(), index, 1);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(hktNow.getFullYear(), index + 1, 0);
          endTime.setHours(23, 59, 59, 999);
        } else {
          // For the entire year (Jan 1 00:00:00 to current day 23:59:59)
          startTime = new Date(hktNow.getFullYear(), 0, 1);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(hktNow);
          endTime.setHours(23, 59, 59, 999);
        }
        break;
      
      case '24h':
        // Last 24 hours from now
        startTime = new Date(hktNow.getTime() - 24 * 60 * 60 * 1000);
        endTime = new Date(hktNow);
        break;
      
      case '7 days':
        // Last 7 days from now
        startTime = new Date(hktNow.getTime() - 7 * 24 * 60 * 60 * 1000);
        endTime = new Date(hktNow);
        break;
      
      case 'All data':
        // All available data
        startTime = new Date(0); // Unix epoch
        endTime = new Date(hktNow);
        break;
      
      default:
        startTime = new Date(0);
        endTime = new Date(hktNow);
    }

    return { 
      startTime: formatDateTime(startTime),
      endTime: formatDateTime(endTime)
    };
  };

  // Fetch data for a given period and update state
  const fetchDataForPeriod = async (period, dataType) => {
    try {
      let chartData = [];
      let number = 0;
      const hktNow = getHKT();

      if (period === 'This day') {
        // Hourly data for current day
        const promises = Array(24).fill().map((_, i) => {
          const { startTime, endTime } = getTimeRange(period, i);
          return dataType === 'records' 
            ? fetchRecordCount(startTime, endTime)
            : fetchUniqueUserCount(startTime, endTime);
        });
        chartData = await Promise.all(promises);
        number = chartData.reduce((sum, val) => sum + val, 0);
      } 
      else if (period === 'This week') {
        // Daily data for current week (Monday to Thursday)
        const daysSinceMonday = hktNow.getDay() === 0 ? 6 : hktNow.getDay() - 1;
        const promises = Array(daysSinceMonday + 1).fill().map((_, i) => {
          const { startTime, endTime } = getTimeRange(period, i);
          return dataType === 'records' 
            ? fetchRecordCount(startTime, endTime)
            : fetchUniqueUserCount(startTime, endTime);
        });
        chartData = await Promise.all(promises);
        number = chartData.reduce((sum, val) => sum + val, 0);
      }
      else if (period === 'This month') {
        // Daily data for current month up to today
        const today = hktNow.getDate();
        const promises = Array(today).fill().map((_, i) => {
          const { startTime, endTime } = getTimeRange(period, i);
          return dataType === 'records' 
            ? fetchRecordCount(startTime, endTime)
            : fetchUniqueUserCount(startTime, endTime);
        });
        chartData = await Promise.all(promises);
        number = chartData.reduce((sum, val) => sum + val, 0);
      }
      else if (period === 'This year') {
        // Monthly data for current year up to current month
        const currentMonth = hktNow.getMonth();
        const promises = Array(currentMonth + 1).fill().map((_, i) => {
          const { startTime, endTime } = getTimeRange(period, i);
          return dataType === 'records' 
            ? fetchRecordCount(startTime, endTime)
            : fetchUniqueUserCount(startTime, endTime);
        });
        chartData = await Promise.all(promises);
        number = chartData.reduce((sum, val) => sum + val, 0);
      }
      else {
        // For 24h, 7 days, and All data
        const { startTime, endTime } = getTimeRange(period);
        number = dataType === 'records' 
          ? await fetchRecordCount(startTime, endTime)
          : await fetchUniqueUserCount(startTime, endTime);
      }

      // Update state
      if (dataType === 'records') {
        setRecordsData(prev => ({ ...prev, [period]: { chart: chartData, number } }));
      } else {
        setUsersData(prev => ({ ...prev, [period]: { chart: chartData, number } }));
      }
    } catch (error) {
      console.error(`Error fetching ${dataType} data for ${period}:`, error);
    }
  };

  // Fetch data every minute and when period changes
  useEffect(() => {
    // Initial fetch for all periods
    allPeriods.forEach(period => {
      fetchDataForPeriod(period, 'records');
      fetchDataForPeriod(period, 'users');
    });

    // Set up interval for updates every minute
    const intervalId = setInterval(() => {
      allPeriods.forEach(period => {
        fetchDataForPeriod(period, 'records');
        fetchDataForPeriod(period, 'users');
      });
    }, 60 * 1000); // 60 seconds

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Chart configuration
  const getChartData = (data, period) => {
    let labels = [];
    const hktNow = getHKT();
    
    if (period === 'This day') {
      labels = Array.from({ length: 24 }, (_, i) => `${i}:00`);
    } 
    else if (period === 'This week') {
      labels = getWeekDayNames(); // ["Mon", "Tue", "Wed", "Thu"]
    }
    else if (period === 'This month') {
      const today = hktNow.getDate();
      labels = Array.from({ length: today }, (_, i) => `${i + 1}`);
    }
    else if (period === 'This year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonth = hktNow.getMonth();
      labels = months.slice(0, currentMonth + 1);
    }

    return {
      labels,
      datasets: [
        {
          label: period === 'This day' ? 'Hourly' : 
                 period === 'This week' ? 'Daily' :
                 period === 'This month' ? 'Daily' :
                 period === 'This year' ? 'Monthly' : '',
          data: data[period].chart,
          backgroundColor: period === 'This day' || period === 'This month' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(24, 144, 255, 0.7)',
          borderColor: '#1890ff',
          borderWidth: 2,
          fill: period === 'This day' || period === 'This month',
          tension: 0.1,
          pointBackgroundColor: '#1890ff',
          pointBorderColor: '#fff',
          pointHoverRadius: 5,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  return (
    <div className="overview-grid">
      <div className="box" style={{ gridColumn: 'span 1', width: '90%', height: '400px' }}>
        <div className="box-header">
          <h3>Records</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)' }}>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>24h</div>
            <div className="big-number">{recordsData['24h'].number.toLocaleString()}</div>
          </div>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>7 days</div>
            <div className="big-number">{recordsData['7 days'].number.toLocaleString()}</div>
          </div>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>All data</div>
            <div className="big-number">{recordsData['All data'].number.toLocaleString()}</div>
          </div>
        </div>
      </div>
      <div className="box" style={{ gridColumn: 'span 3', width: '95%', height: '400px' }}>
        <div className="box-header">
          <h3>Records</h3>
          <select
            value={recordsPeriod}
            onChange={(e) => setRecordsPeriod(e.target.value)}
            className="period-chooser"
          >
            {chartPeriods.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>
        <div className="chart-container">
          {['This day', 'This month'].includes(recordsPeriod) ? (
            <Line data={getChartData(recordsData, recordsPeriod)} options={chartOptions} />
          ) : (
            <Bar data={getChartData(recordsData, recordsPeriod)} options={chartOptions} />
          )}
        </div>
      </div>
      <div className="box" style={{ gridColumn: 'span 1', width: '90%', height: '400px' }}>
        <div className="box-header">
          <h3>Users</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)' }}>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>24h</div>
            <div className="big-number">{usersData['24h'].number.toLocaleString()}</div>
          </div>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>7 days</div>
            <div className="big-number">{usersData['7 days'].number.toLocaleString()}</div>
          </div>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '8px' }}>All data</div>
            <div className="big-number">{usersData['All data'].number.toLocaleString()}</div>
          </div>
        </div>
      </div>
      <div className="box" style={{ gridColumn: 'span 3', width: '95%', height: '400px' }}>
        <div className="box-header">
          <h3>Users</h3>
          <select
            value={usersPeriod}
            onChange={(e) => setUsersPeriod(e.target.value)}
            className="period-chooser"
          >
            {chartPeriods.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>
        <div className="chart-container">
          {['This day', 'This month'].includes(usersPeriod) ? (
            <Line data={getChartData(usersData, usersPeriod)} options={chartOptions} />
          ) : (
            <Bar data={getChartData(usersData, usersPeriod)} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
}